import { Inject, Injectable } from '@angular/core';
import { createTRPCProxyClient } from '@trpc/client';
import { firstValueFrom, from, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Favorite, SearchEngineProduct } from '../../models';
import { getUpdatedFavoriteItems, MAX_FAVORITES_ITEMS } from '../../utils/favorite.utils';
import { getTRPCClientOptions } from '../../utils/trpc.utils';
import { FirestoreService } from '../firestore/firestore.service';
import { LocalDataKey, LocalStorageService } from '../local-storage/local-storage.service';
import { SearchService } from '../search/search.service';
import { SessionsService } from '../sessions/sessions.service';
import { AddFavoriteItemData, FavoriteDetail, FavoritesService, LocalFavorite, RemoveFavoriteItemData, SynchronizeFavoritesData } from './favorites.service';

@Injectable()
export class FavoritesServiceApi implements FavoritesService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private client: any;
  private favoriteDetail: Observable<FavoriteDetail | null>;

  constructor(
    @Inject('appFirestore') private firestore: FirestoreService,
    private localStorageService: LocalStorageService,
    private searchService: SearchService,
    private sessionsService: SessionsService,
  ) {
    this.client = createTRPCProxyClient(getTRPCClientOptions(`${environment.apiUrl}/favorites`, () => this.sessionsService.getAccessToken()));
    this.favoriteDetail = this.sessionsService.getSession().pipe(switchMap((session) => {
      if (session) {
        return this.firestore.doc<FavoriteDetail>(`favorites/${session.userId}`).pipe(switchMap((favorite) => this.getChildren(favorite)));
      }
      return this.getLocalFavorite().pipe(switchMap((favorite) => this.getChildren(favorite)));
    }));
  }

  async addItem(data: AddFavoriteItemData): Promise<void> {
    const session = await firstValueFrom(this.sessionsService.getSession());
    if (session) {
      return this.client.addItem.mutate(data).then();
    }
    let localFavorite = await firstValueFrom(this.getLocalFavorite());
    if (!localFavorite) {
      localFavorite = {
        items: [{
          ...data,
          addedAt: new Date(),
        }],
      };
    } else {
      localFavorite = {
        ...localFavorite,
        items: getUpdatedFavoriteItems({
          action: 'add',
          items: localFavorite.items,
          addItemData: data,
        }),
      };
    }
    this.localStorageService.set<LocalFavorite>(LocalDataKey.Favorites, localFavorite);
  }

  get(): Observable<FavoriteDetail | null> {
    return this.favoriteDetail;
  }

  hasFavorite(resourceType: string, resourceId: string): Observable<boolean> {
    return this.favoriteDetail.pipe(map((favorite) => !!favorite?.items.some((item) => item.resourceType === resourceType && item.resourceId === resourceId)));
  }

  hasFavoriteItems(): Observable<boolean> {
    return this.favoriteDetail.pipe(map((favorites) => !!favorites?.items.length));
  }

  async removeItem(data: RemoveFavoriteItemData): Promise<void> {
    const session = await firstValueFrom(this.sessionsService.getSession());
    if (session) {
      return this.client.removeItem.mutate(data).then();
    }
    let localFavorite = await firstValueFrom(this.getLocalFavorite());
    if (!localFavorite) {
      localFavorite = {
        items: [],
      };
    } else {
      localFavorite = {
        ...localFavorite,
        items: getUpdatedFavoriteItems({
          action: 'remove',
          items: localFavorite.items,
          removeItemData: data,
        }),
      };
    }
    this.localStorageService.set<LocalFavorite>(LocalDataKey.Favorites, localFavorite);
  }

  async synchronize(): Promise<void> {
    const localFavorite = await firstValueFrom(this.getLocalFavorite());
    if (!localFavorite) {
      return Promise.resolve();
    }
    if (localFavorite && localFavorite.items.length) {
      const data = { items: localFavorite.items.filter((item) => item.resourceType === 'product') } as SynchronizeFavoritesData;
      return this.client.synchronize.mutate(data).then();
    }
  }

  private getChildren(favorite: Favorite | LocalFavorite | null): Observable<FavoriteDetail | null> {
    if (!favorite) {
      return of(null);
    }
    return from(this.searchService.search<SearchEngineProduct>('products', {
      filters: [
        {
          field: 'id',
          operator: 'in',
          value: favorite.items.map((item) => item.resourceId),
        },
      ],
      pagination: {
        documentsPerPage: MAX_FAVORITES_ITEMS,
      },
    })).pipe(map((paginable) => {
      const detailItems = favorite.items.map((item) => {
        if (item.resourceType === 'product') {
          return {
            ...item,
            resource: paginable.data.find((product) => product.id === item.resourceId) ?? null,
          };
        }
        return {
          ...item,
          resource: null,
        };
      }).filter((item) => item.resource);
      return {
        items: detailItems,
      };
    }));
  }

  private getLocalFavorite(): Observable<LocalFavorite | null> {
    return this.localStorageService.get<LocalFavorite>(LocalDataKey.Favorites).pipe(map((localFavorite) => {
      if (localFavorite) {
        localFavorite.items = localFavorite.items.map((item) => {
          if (item.addedAt) {
            item.addedAt = new Date(item.addedAt);
          }
          return item;
        });
      }
      return localFavorite;
    }));
  }
}
